import { useState, useEffect, useRef } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Plus, User, Pencil, Eye, UserX, UserCheck, Truck, PauseCircle, Trash2, Briefcase, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DriverFormModal from './DriverFormModal';
import DriverDetailModal from './DriverDetailModal';

// Small compliance status pill -- green when the thing is on file/done,
// amber otherwise. Used for W-9-on-file on Owner Operator rows (W-4/W-2
// compliance for Company Drivers + Staff lives in Accounting now).
const ComplianceBadge = ({ ok, okLabel, missingLabel }) => (
  <span
    className={cn(
      "text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0",
      ok ? "text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30" : "text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30"
    )}
  >
    {ok ? okLabel : missingLabel}
  </span>
);

export default function DriverManagement({ openDriverId }) {
  const openedTargetRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [formInitialTab, setFormInitialTab] = useState('profile');
  const [viewDriver, setViewDriver] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadDrivers(); }, []);

  // Auto-open a driver's DQF editor when navigated here from an expiry alert
  useEffect(() => {
    if (!openDriverId || openedTargetRef.current === openDriverId || drivers.length === 0) return;
    const target = drivers.find(d => d.id === openDriverId);
    if (target) {
      openedTargetRef.current = openDriverId;
      setEditDriver(target);
      setFormInitialTab('dqf');
      setShowForm(true);
    }
  }, [openDriverId, drivers]);

  const loadDrivers = async () => {
    setLoading(true);
    try {
      // Sort by most recently updated so the latest version of each profile wins
      const data = await client.entities.DriverProfile.list('-updated_date', 200);
      // Deduplicate by email — if Marcus has two records, keep the most recently updated one
      const seen = new Set();
      const deduped = data.filter(d => {
        const key = d.user_email?.toLowerCase() || d.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setDrivers(deduped);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (driver) => {
    if (!window.confirm(`Permanently delete ${driver.full_name}'s profile? This cannot be undone.`)) return;
    // No try/catch here previously -- a failed delete (e.g. blocked by a
    // foreign key from Settlements/DQF records still pointing at this
    // driver) threw silently with no toast and no row removed, so the row
    // just sat there looking "stuck," indistinguishable from the delete
    // never having been attempted at all.
    try {
      await client.entities.DriverProfile.delete(driver.id);
      setDrivers(prev => prev.filter(d => d.id !== driver.id));
      toast.success(`${driver.full_name} deleted.`);
    } catch (err) {
      toast.error(err?.message || `Could not delete ${driver.full_name}.`);
    }
  };

  // Suspend an active driver (drug test / accident investigation) — keeps Approved status
  const handleSuspend = async (driver) => {
    if (!window.confirm(`Suspend ${driver.full_name}? They will be placed on hold pending investigation. You can reactivate them at any time.`)) return;
    await client.entities.DriverProfile.update(driver.id, { is_active: false, employment_status: 'On Hold' });
    toast.info(`${driver.full_name} has been suspended.`);
    loadDrivers();
  };

  // Reactivate a suspended driver
  const handleReactivate = async (driver) => {
    await client.entities.DriverProfile.update(driver.id, { is_active: true, employment_status: 'Active' });
    toast.success(`${driver.full_name} has been reactivated.`);
    loadDrivers();
  };

  // Send a rejected APPLICANT (never hired -- no termination_date; see the
  // handleTerminate comment above on why terminated ex-employees are
  // deliberately excluded from this) back to Pending Review, on their
  // existing record. Previously the only way to reconsider a rejected
  // driver was to delete their profile and re-add them from scratch,
  // throwing away whatever DQF/CDL/employment info was already on file.
  const handleReconsider = async (driver) => {
    if (!window.confirm(`Move ${driver.full_name} back to Pending Review?`)) return;
    await client.entities.DriverProfile.update(driver.id, { approval_status: 'Pending' });
    toast.success(`${driver.full_name} moved back to Pending Review.`);
    loadDrivers();
  };

  // Permanently terminate — sets termination date and reason
  const handleTerminate = async (driver) => {
    if (!window.confirm(`Terminate ${driver.full_name}? This is permanent and will move them to the Terminated tab.`)) return;
    const reason = window.prompt('Termination reason (required for DOT records):', 'Voluntary resignation');
    if (reason === null) return; // cancelled
    await client.entities.DriverProfile.update(driver.id, {
      approval_status: 'Rejected',
      is_active: false,
      employment_status: 'Terminated',
      termination_date: new Date().toISOString().split('T')[0],
      termination_reason: reason || 'Unspecified',
    });
    toast.info(`${driver.full_name} has been terminated.`);
    loadDrivers();
  };

  // Officially hire an approved driver — stamps the hire date used for
  // DOT/payroll records. Separate from approval: a driver can be
  // Approved (DQF-eligible) before their actual start date is set.
  const handleHire = async (driver) => {
    const today = new Date().toISOString().split('T')[0];
    const dateInput = window.prompt('Hire date (YYYY-MM-DD):', today);
    if (dateInput === null) return; // cancelled
    const hireDate = /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim()) ? dateInput.trim() : today;
    await client.entities.DriverProfile.update(driver.id, {
      hire_date: hireDate,
      employment_status: 'Active',
      is_active: true,
    });
    toast.success(`${driver.full_name} hired as of ${hireDate}.`);
    loadDrivers();
  };

  // Only approved + currently active
  const activeDrivers = drivers.filter(d => d.approval_status === 'Approved' && d.is_active !== false);
  // Suspended = was approved but temporarily deactivated (no termination date)
  const onHoldDrivers = drivers.filter(d => d.approval_status === 'Approved' && d.is_active === false && !d.termination_date);
  // Terminated = permanently ended
  const terminatedDrivers = drivers.filter(d => d.termination_date != null);

  const filtered =
    filter === 'active' ? activeDrivers :
    filter === 'onhold' ? onHoldDrivers :
    filter === 'terminated' ? terminatedDrivers :
    // 'all' — exclude new applicants (approval_status: Pending) — those belong in Approvals tab
    drivers.filter(d => d.approval_status !== 'Pending');

  const statusBadge = (driver) => {
    if (driver.termination_date) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 flex-shrink-0">Terminated</span>;
    if (driver.approval_status === 'Approved' && driver.is_active === false) return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 flex-shrink-0">On Hold</span>;
    if (driver.approval_status === 'Approved') return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 flex-shrink-0">Active</span>;
    return null;
  };

  const iconColor = (driver) => {
    if (driver.termination_date) return { bg: 'bg-red-500/10', icon: 'text-red-400' };
    if (driver.is_active === false) return { bg: 'bg-yellow-500/10', icon: 'text-yellow-400' };
    return { bg: 'bg-green-500/20', icon: 'text-green-400' };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Filter tabs */}
        <div className="flex items-center gap-1 glass-card p-1 rounded-xl">
          {[
            ['active', `Active (${activeDrivers.length})`, 'text-green-400 bg-green-500/20'],
            ['onhold', `On Hold (${onHoldDrivers.length})`, 'text-yellow-400 bg-yellow-500/20'],
            ['terminated', `Terminated (${terminatedDrivers.length})`, 'text-red-400 bg-red-500/20'],
            ['all', `All (${drivers.filter(d => d.approval_status !== 'Pending').length})`, 'bg-primary text-primary-foreground'],
          ].map(([val, label, activeClass]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                filter === val ? activeClass : "text-muted-foreground hover:text-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { setEditDriver(null); setShowForm(true); }} className="bg-primary text-primary-foreground">
            <Plus className="w-4 h-4 mr-2" /> Add Driver
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="glass-card p-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <User className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">No drivers found.</p>
          {filter === 'onhold' && <p className="text-muted-foreground text-xs mt-1">Drivers suspended for investigation will appear here.</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {[
            { type: 'Company Driver', label: 'Company Drivers', color: 'text-blue-400' },
            { type: 'Owner Operator', label: 'Owner Operators', color: 'text-purple-400' },
            { type: '__unset__', label: 'Uncategorized', color: 'text-muted-foreground' },
          ].map(({ type, label, color }) => {
            const group = type === '__unset__'
              ? filtered.filter(d => !d.driver_type)
              : filtered.filter(d => d.driver_type === type);
            if (group.length === 0) return null;
            return (
              <div key={type}>
                <p className={cn("text-xs font-semibold uppercase tracking-widest mb-2 px-1", color)}>{label} ({group.length})</p>
                <div className="glass-card overflow-hidden">
                  <div className="divide-y divide-border">
                    {group.map(driver => {
                      const colors = iconColor(driver);
                      return (
                        <div key={driver.id} className="flex items-center justify-between px-5 py-4 gap-3">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0", colors.bg)}>
                              <User className={cn("w-5 h-5", colors.icon)} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-foreground truncate">{driver.full_name}</p>
                                {statusBadge(driver)}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                {driver.employee_number && <p className="text-xs text-muted-foreground">Emp #{driver.employee_number}</p>}
                                {driver.user_email && <p className="text-xs text-muted-foreground">{driver.user_email}</p>}
                                {driver.phone && <p className="text-xs text-muted-foreground">{driver.phone}</p>}
                                <div className="flex items-center gap-1">
                                  <Truck className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {driver.truck_info
                                      ? `${driver.truck_info}${driver.truck_vin ? ` · ${driver.truck_vin}` : ''}`
                                      : driver.truck_type}
                                  </span>
                                </div>
                                {driver.mc_number && <span className="text-xs text-muted-foreground">MC: {driver.mc_number}</span>}
                                {driver.hire_date && <span className="text-xs text-muted-foreground">Hired: {driver.hire_date}</span>}
                                {driver.termination_date && <span className="text-xs text-red-400/80">Terminated: {driver.termination_date}</span>}
                              </div>
                              {/* Owner Operators are 1099 contractors -- W-9 is a
                                  hiring/onboarding document, so it stays here.
                                  Company Driver W-4/W-2 tax compliance lives in
                                  Accounting now (Accounting > W-2 & W-4 Compliance). */}
                              {driver.driver_type === 'Owner Operator' && (
                                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                  <ComplianceBadge ok={!!driver.w9_signature} okLabel="W-9 on file" missingLabel="No W-9 on file" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {driver.approval_status === 'Approved' && !driver.hire_date && !driver.termination_date && (
                              <button
                                onClick={() => handleHire(driver)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title="Hire (set hire date)"
                              >
                                <Briefcase className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => setViewDriver(driver)}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditDriver(driver); setFormInitialTab('profile'); setShowForm(true); }}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(driver)}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Delete Profile"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            {/* Active driver — can suspend or terminate */}
                            {driver.approval_status === 'Approved' && driver.is_active !== false && (
                              <>
                                <button
                                  onClick={() => handleSuspend(driver)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                                  title="Suspend (Drug Test / Investigation)"
                                >
                                  <PauseCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleTerminate(driver)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                  title="Terminate"
                                >
                                  <UserX className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {/* Rejected applicant (never hired -- not a terminated
                                ex-employee) — can be reconsidered back into Approvals
                                on their existing record instead of delete + re-add. */}
                            {driver.approval_status === 'Rejected' && !driver.termination_date && (
                              <button
                                onClick={() => handleReconsider(driver)}
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title="Reconsider (move back to Pending Review)"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            {/* On-hold driver — can reactivate or terminate */}
                            {driver.approval_status === 'Approved' && driver.is_active === false && !driver.termination_date && (
                              <>
                                <button
                                  onClick={() => handleReactivate(driver)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-green-400 hover:bg-green-500/10 transition-colors"
                                  title="Reactivate"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleTerminate(driver)}
                                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                  title="Terminate"
                                >
                                  <UserX className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <DriverFormModal
          driver={editDriver}
          initialTab={formInitialTab}
          onClose={() => { setShowForm(false); setEditDriver(null); }}
          onSaved={() => { loadDrivers(); }}
        />
      )}

      {viewDriver && (
        <DriverDetailModal
          driver={viewDriver}
          onClose={() => setViewDriver(null)}
        />
      )}
    </div>
  );
}
